import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Building2, Users, Calendar, Settings, LogOut,
  ChevronDown, Menu, X, Bell, MapPin, Layers, Grid3X3, UserCog,
  ClipboardList, Clock, FileText, Shield, Home, Upload
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isSuperadmin = user?.role === 'superadmin';

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'superadmin'] },
    { path: '/admin/companies', icon: Building2, label: 'Empresas', roles: ['superadmin'] },
    { path: '/admin/sedes', icon: MapPin, label: 'Sedes', roles: ['admin', 'superadmin'] },
    { path: '/admin/floors', icon: Layers, label: 'Pisos y Zonas', roles: ['admin', 'superadmin'] },
    { path: '/admin/spaces', icon: Grid3X3, label: 'Espacios', roles: ['admin', 'superadmin'] },
    { path: '/admin/users', icon: Users, label: 'Usuarios', roles: ['admin', 'superadmin'] },
    { path: '/admin/teams', icon: UserCog, label: 'Equipos y Áreas', roles: ['admin', 'superadmin'] },
    { path: '/admin/capacity', icon: Settings, label: 'Capacidad Diaria', roles: ['admin', 'superadmin'] },
    { path: '/admin/rules', icon: ClipboardList, label: 'Reglas de Asignación', roles: ['admin', 'superadmin'] },
    { path: '/admin/scheduler', icon: Calendar, label: 'Scheduler', roles: ['admin', 'superadmin'] },
    { path: '/admin/assignments', icon: Calendar, label: 'Asignaciones', roles: ['admin', 'superadmin'] },
    { path: '/admin/requests', icon: Clock, label: 'Solicitudes', roles: ['admin', 'superadmin'] },
    { path: '/admin/waitlist', icon: Clock, label: 'Lista de Espera', roles: ['admin', 'superadmin'] },
    { path: '/admin/reports', icon: FileText, label: 'Reportes', roles: ['admin', 'superadmin'] },
    { path: '/admin/audit', icon: Shield, label: 'Auditoría', roles: ['admin', 'superadmin'] },
    { path: '/admin/bulk-upload', icon: Upload, label: 'Carga Masiva', roles: ['admin', 'superadmin'] },
  ];

  const filteredMenu = menuItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-slate-100 rounded-sm"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <img src="/assets/logo-am-office.png" alt="AM Office" className="h-8 w-auto" />
        <Bell className="w-5 h-5 text-slate-500" />
      </div>

      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-white border-r border-slate-200 transition-all duration-300 z-40 
        ${sidebarOpen ? 'w-64' : 'w-20'} hidden lg:block`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          {sidebarOpen && (
            <div className="flex-1 flex items-center justify-center">
              <img 
                src="/assets/logo-am-office.png" 
                alt="AM Office" 
                className="h-10 w-auto"
              />
            </div>
          )}
          {!sidebarOpen && (
            <img 
              src="/assets/logo-am-office.png" 
              alt="AM Office" 
              className="h-7 w-auto object-contain"
              style={{ maxWidth: '40px', objectPosition: 'left' }}
            />
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 hover:bg-slate-100 rounded-sm"
            data-testid="sidebar-toggle"
          >
            <ChevronDown className={`w-5 h-5 transition-transform ${sidebarOpen ? 'rotate-90' : '-rotate-90'}`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-2 space-y-1 overflow-y-auto h-[calc(100%-8rem)]">
          {filteredMenu.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''} ${!sidebarOpen ? 'justify-center' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <div className={`flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
            <div className="w-10 h-10 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-slate-100 rounded-sm text-slate-500 hover:text-red-500"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white">
            <div className="h-16 flex items-center px-4 border-b border-slate-200">
              <img src="/assets/logo-am-office.png" alt="AM Office" className="h-8 w-auto" />
            </div>
            <nav className="p-2 space-y-1">
              {filteredMenu.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 rounded-sm"
              >
                <LogOut className="w-5 h-5" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} pt-16 lg:pt-0`}>
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
